import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './app/auth';
import { Layout } from './app/Layout';
import { Loading } from './components/ui';
import { requestPersistentStorage } from './drafts/store';
import { ApiError } from './lib/errors';
import { startServiceWorker } from './lib/sw';
import { captureInstallPrompt } from './platform/install';
import { AccountScreen } from './screens/Account';
import { AddReceiptScreen } from './screens/AddReceipt';
import { AdminScreen } from './screens/Admin';
import { ArchiveScreen } from './screens/Archive';
import { CancelRefundScreen } from './screens/CancelRefund';
import { DashboardScreen } from './screens/Dashboard';
import { DraftsScreen } from './screens/Drafts';
import { NotificationsScreen } from './screens/Notifications';
import { PaymentsScreen } from './screens/Payments';
import { InstallScreen } from './screens/Install';
import { AcceptInviteScreen, FirstLoginScreen, LoginScreen, PasswordResetScreen } from './screens/Login';
import { PeopleScreen, PersonDetailScreen } from './screens/People';
import { PoolScreen } from './screens/Pool';
import { ReceiptDetailScreen } from './screens/ReceiptDetail';
import { StatsScreen } from './screens/Stats';
import './styles.css';

// Odaklanınca yenileme + görünür ekranda 30 sn sorgulama (PDF §02 "ortak davranış").
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchInterval: () => (document.visibilityState === 'visible' ? 30_000 : false),
      staleTime: 10_000,
      retry: (count, err) => !(err instanceof ApiError) && count < 2,
    },
    mutations: { retry: false },
  },
});

function RequireAuth({ children, admin = false }: { children: ReactNode; admin?: boolean }) {
  const { me, loading, offline, isAdmin, reload } = useAuth();
  if (loading) return <div className="main"><Loading /></div>;
  if (!me && offline) {
    return (
      <div className="main stack" style={{ maxWidth: 480, margin: '15vh auto 0', textAlign: 'center' }}>
        <h2>Sunucuya şu an ulaşılamıyor</h2>
        <p className="muted">İnternet bağlantınızı kontrol edin. Sistem güncelleniyor olabilir; birkaç dakika içinde açılır.
          Oturumunuz ve taslaklarınız korunuyor.</p>
        <button className="btn primary" onClick={() => void reload()}>Tekrar dene</button>
      </div>
    );
  }
  if (!me) return <Navigate to="/giris" replace />;
  if (admin && !isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/giris" element={<LoginScreen />} />
      <Route path="/ilk-giris" element={<FirstLoginScreen />} />
      <Route path="/kur" element={<InstallScreen />} />
      <Route path="/davet/:token" element={<AcceptInviteScreen />} />
      <Route path="/sifre-sifirla" element={<PasswordResetScreen />} />
      <Route path="/sifre-sifirla/:token" element={<PasswordResetScreen />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<DashboardScreen />} />
        <Route path="havuz" element={<PoolScreen />} />
        <Route path="fis/:id" element={<ReceiptDetailScreen />} />
        <Route path="fis-ekle" element={<AddReceiptScreen />} />
        <Route path="fis-ekle/:draftId" element={<AddReceiptScreen />} />
        <Route path="taslaklar" element={<DraftsScreen />} />
        <Route path="kisiler" element={<PeopleScreen />} />
        <Route path="kisiler/:userId" element={<PersonDetailScreen />} />
        <Route path="arsiv" element={<ArchiveScreen />} />
        <Route path="istatistik" element={<StatsScreen />} />
        <Route path="iptal-iade" element={<CancelRefundScreen />} />
        <Route path="hesabim" element={<AccountScreen />} />
        <Route path="odemeler" element={<PaymentsScreen />} />
        <Route path="bildirimler" element={<NotificationsScreen />} />
        <Route path="yonetim" element={<RequireAuth admin><AdminScreen /></RequireAuth>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// "Uygulamayı yükle" istemi sayfa açılır açılmaz gelebilir; erken yakalanır.
captureInstallPrompt();
// Servis çalışanı giriş beklenmeden kaydolur (kurulum, çevrimdışı açılış, bildirimler, güncelleme bandı).
startServiceWorker();
// Taslak fotoğrafları disk temizliğinde silinmesin diye kalıcı depolama istenir.
void requestPersistentStorage();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
